import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { InventoryService } from '../../core/services/inventory.service';
import { OrderService } from '../../core/services/order.service';
import { StoreService } from '../../core/services/store.service';
import { ApiResponse } from '../../core/models/product.models';
import { RevenueTrendItem, CategoryAnalyticsItem } from '../../core/models/product.models';
import { OrderDto } from '../../core/models/order.models';
import { StoreLiteDto } from '../../core/models/store.models';

// ── /api/Dashboard response types ────────────────────────────────────────────
export interface DailySalesDto {
  date: string;
  total: number;
  orders: number;
}
export interface TopProductDto {
  productName: string;
  quantitySold: number;
  revenue: number;
}
export interface LowStockItemDto {
  productName: string;
  storeName: string;
  quantity: number;
}
/**
 * Sotuv narxi tannarxdan past mahsulot — har sotuvda zarar.
 * Odatda aksiya emas, narx kiritishdagi xato (quti narxi vs dona narxi).
 */
export interface BelowCostItemDto {
  productName: string;
  sellPrice: number;
  cost: number;
  lossPerUnit: number;
  stockQuantity: number;
}
export interface PaymentBreakdownDto {
  type: string;
  amount: number;
  count: number;
}
export interface HourlySalesDto {
  hour: number;
  total: number;
  orders: number;
}
export interface TopDebtorDto {
  name: string;
  phone: string | null;
  amount: number;
  orderCount: number;
}
export interface CashierPerfDto {
  name: string;
  sales: number;
  orders: number;
}
export interface ExpenseBreakdownDto {
  category: string;
  amount: number;
  count: number;
}

export interface DashboardStatsDto {
  todaySales: number;
  todayOrders: number;
  todayProfit: number;
  totalSales: number;
  totalOrders: number;
  totalProfit: number;
  activeProducts: number;
  previousSales: number;
  previousOrders: number;
  previousProfit: number;
  totalOutstandingDebt: number;
  debtOrderCount: number;
  weeklyChart: DailySalesDto[];
  topProducts: TopProductDto[];
  lowStockAlerts: LowStockItemDto[];
  belowCostAlerts: BelowCostItemDto[];
  paymentBreakdown: PaymentBreakdownDto[];
  hourlySales: HourlySalesDto[];
  topDebtors: TopDebtorDto[];
  cashierPerformance: CashierPerfDto[];
  totalExpenses: number;
  expenseCount: number;
  writeOffLoss: number;
  writeOffCount: number;
  netProfit: number;
  previousExpenses: number;
  previousWriteOffLoss: number;
  expenseBreakdown: ExpenseBreakdownDto[];
  /**
   * Yuqoridagi BARCHA raqamlar shu valyutada. Aralash summa hech qachon
   * qaytarilmaydi — turli valyutadagi sonlarni qo'shib bo'lmaydi.
   */
  currency: string;
  /** Tanlagich uchun: tenant'da yoqilgan va savdo bo'lgan valyutalar. */
  availableCurrencies: string[];
  /** Davr savdosi — har valyutada alohida (filtrdan mustaqil). */
  salesByCurrency: CurrencyAmountDto[];
  /** Davr yalpi foydasi — har valyutada alohida. */
  profitByCurrency: CurrencyAmountDto[];
}

export interface CurrencyAmountDto {
  currency: string;
  amount: number;
}

export interface DashboardFilter {
  from: Date;
  to: Date;
  storeIds?: string[];
  /** Bo'sh bo'lsa backend tenant kotirovka valyutasini oladi. */
  currency?: string;
}

/** Sahifa bo'limining yuklanish holati. `forbidden` — xodimda ruxsat yo'q (kutilgan). */
export type SectionStatus = 'ok' | 'forbidden' | 'error';

export interface DashboardData {
  stats: DashboardStatsDto;
  trend: RevenueTrendItem[];
  categories: CategoryAnalyticsItem[];
  orders: OrderDto[];
  status: {
    stats: SectionStatus;
    trend: SectionStatus;
    categories: SectionStatus;
    orders: SectionStatus;
  };
}

const EMPTY_STATS: DashboardStatsDto = {
  todaySales: 0,
  todayOrders: 0,
  todayProfit: 0,
  totalSales: 0,
  totalOrders: 0,
  totalProfit: 0,
  currency: '',
  availableCurrencies: [],
  salesByCurrency: [],
  profitByCurrency: [],
  activeProducts: 0,
  previousSales: 0,
  previousOrders: 0,
  previousProfit: 0,
  totalOutstandingDebt: 0,
  debtOrderCount: 0,
  weeklyChart: [],
  topProducts: [],
  lowStockAlerts: [],
  belowCostAlerts: [],
  paymentBreakdown: [],
  hourlySales: [],
  topDebtors: [],
  cashierPerformance: [],
  totalExpenses: 0,
  expenseCount: 0,
  writeOffLoss: 0,
  writeOffCount: 0,
  netProfit: 0,
  previousExpenses: 0,
  previousWriteOffLoss: 0,
  expenseBreakdown: [],
};

/** .NET DateTime? model binding ISO 8601 to'liq formatni talab qiladi */
function toISO(d: Date): string {
  return d.toISOString();
}

/** Kun oxiriga (23:59:59.999) qo'yib yuborish uchun */
function toISOEndOfDay(d: Date): string {
  const end = new Date(d);
  end.setHours(23, 59, 59, 999);
  return end.toISOString();
}

function statusFromError(err: unknown): SectionStatus {
  const s = err instanceof HttpErrorResponse ? err.status : 0;
  return s === 401 || s === 403 ? 'forbidden' : 'error';
}

interface Wrapped<T> {
  value: T;
  status: SectionStatus;
}

function wrap<T>(source: Observable<T>, fallback: T): Observable<Wrapped<T>> {
  return source.pipe(
    map((value) => ({ value, status: 'ok' as SectionStatus })),
    catchError((err) => of({ value: fallback, status: statusFromError(err) })),
  );
}

@Injectable({ providedIn: 'root' })
export class DashboardService {
  private http = inject(HttpClient);
  private inventory = inject(InventoryService);
  private orders = inject(OrderService);
  private storeService = inject(StoreService);

  private readonly dashboardUrl = `${environment.apiUrl}/api/Dashboard`;

  // ── Main stats from /api/Dashboard ───────────────────────────────────────
  private getStats(filter: DashboardFilter): Observable<DashboardStatsDto> {
    let params = new HttpParams()
      .set('startDate', toISO(filter.from))
      .set('endDate', toISOEndOfDay(filter.to));
    if (filter.storeIds?.length)
      filter.storeIds.forEach((id) => (params = params.append('storeIds', id)));
    if (filter.currency) params = params.set('currency', filter.currency);

    return this.http
      .get<ApiResponse<DashboardStatsDto>>(this.dashboardUrl, { params })
      .pipe(map((r) => r.data ?? EMPTY_STATS));
  }

  // ── Revenue + profit trend (oxirgi 12 oy — filtr sanasidan mustaqil) ──────
  private getRevenueTrend(filter: DashboardFilter): Observable<RevenueTrendItem[]> {
    return this.inventory
      .getRevenueTrend(filter.storeIds, undefined, undefined, filter.currency)
      .pipe(map((r) => r.data ?? []));
  }

  // ── Category analytics (filtr sanasi + do'kon) ───────────────────────────
  private getCategoryAnalytics(filter: DashboardFilter): Observable<CategoryAnalyticsItem[]> {
    return this.inventory
      .getCategoryAnalytics(
        filter.storeIds,
        toISO(filter.from),
        toISOEndOfDay(filter.to),
        filter.currency,
      )
      .pipe(map((r) => r.data ?? []));
  }

  // ── Recent orders ─────────────────────────────────────────────────────────
  private getRecentOrders(filter: DashboardFilter): Observable<OrderDto[]> {
    return this.orders
      .getOrders(1, 10, filter.storeIds, undefined, toISO(filter.from), toISOEndOfDay(filter.to))
      .pipe(map((r) => r.data?.items ?? []));
  }

  // ── Stores dropdown ───────────────────────────────────────────────────────
  getStores(): Observable<StoreLiteDto[]> {
    return this.storeService.getAllLite().pipe(
      map((r) => r.data ?? []),
      catchError(() => of([])),
    );
  }

  // ── Load all in parallel, per-section status preserved ───────────────────
  loadAll(filter: DashboardFilter): Observable<DashboardData> {
    return forkJoin({
      stats: wrap(this.getStats(filter), EMPTY_STATS),
      trend: wrap(this.getRevenueTrend(filter), [] as RevenueTrendItem[]),
      categories: wrap(this.getCategoryAnalytics(filter), [] as CategoryAnalyticsItem[]),
      orders: wrap(this.getRecentOrders(filter), [] as OrderDto[]),
    }).pipe(
      map((r) => ({
        stats: r.stats.value,
        trend: r.trend.value,
        categories: r.categories.value,
        orders: r.orders.value,
        status: {
          stats: r.stats.status,
          trend: r.trend.status,
          categories: r.categories.status,
          orders: r.orders.status,
        },
      })),
    );
  }
}
